import { Link } from "wouter";
import { SEOHead, SOFTWARE_SCHEMA } from "@/components/seo-head";
import { Button } from "@/components/ui/button";
import { SeoPageHeader } from "@/components/seo-page-header";
import {
  ChevronRight,
  CheckCircle2,
  FileText,
  ArrowRight,
  Ruler,
  ClipboardList,
} from "lucide-react";

const PAGE_URL = "https://thepropertool.com/quote-from-drawings";

const SOFTWARE_APP_SCHEMA = {
  ...SOFTWARE_SCHEMA,
  "name": "The Proper Tool | Quote from Drawings: Smart Takeoff for UK Builders",
  "description": "Upload your plans or PDF drawings and get a fully-priced construction quote for review. Smart takeoff and pricing built for UK builders.",
  "url": PAGE_URL,
};

const BREADCRUMB_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://thepropertool.com/" },
    { "@type": "ListItem", "position": 2, "name": "Smart Auto Quoting", "item": "https://thepropertool.com/ai-quoting-software" },
    { "@type": "ListItem", "position": 3, "name": "Quote from Drawings", "item": PAGE_URL },
  ],
};

const FAQ_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "Can Auto Quote read construction drawings?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes. The Proper Tool's Auto Quote reads uploaded PDFs of structural drawings and architect's plans. It identifies beam sizes, spans, floor areas, connection details, and project dimensions directly from the drawing. Auto Quote is trained on UK residential construction drawings, not generic documents, so it understands structural notation, section views, and plan annotations used by UK engineers and architects.",
      },
    },
    {
      "@type": "Question",
      "name": "How accurate is Smart takeoff from drawings?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Accuracy improves with drawing quality. Clear, dimensioned PDF plans with structural annotations give the best results; Auto Quote can extract beam specifications, floor areas, and structural details to produce a precise, itemised quote. Photos of hand-drawn sketches or low-resolution scans work too, but with less precision. The Proper Tool's editable quote lets you check the scope, quantities and pricing before it goes to the client.",
      },
    },
    {
      "@type": "Question",
      "name": "What file types and plan formats work?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "The Proper Tool accepts PDF files (the most common format for architect's and engineer's drawings), as well as JPG, PNG, and WEBP images. Multi-page PDFs work; Auto Quote reads all pages. Structural drawing sets, floor plans, elevation drawings, and section views are all supported. For best results, use the original PDF rather than a scan of a printout.",
      },
    },
    {
      "@type": "Question",
      "name": "Does it produce a Bill of Quantities?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "The Proper Tool produces a fully itemised quote structured like a Bill of Quantities: every trade, every line item, with quantities and unit rates. It follows the NRM2 (New Rules of Measurement 2) conventions used across UK construction: works are ordered by trade and element, with labour, materials, and plant broken out per item. The output is not a formal NRM2 document, but it is structured and itemised in the same way, making it straightforward for quantity surveyors and contractors to review.",
      },
    },
  ],
};

const FAQS = [
  {
    q: "Can Auto Quote read construction drawings?",
    a: "Yes. The Proper Tool's Auto Quote reads uploaded PDFs of structural drawings and architect's plans. It identifies beam sizes, spans, floor areas, connection details, and project dimensions directly from the drawing. Auto Quote is trained on UK residential construction drawings, not generic documents, so it understands structural notation, section views, and plan annotations used by UK engineers and architects.",
  },
  {
    q: "How accurate is Smart takeoff from drawings?",
    a: "Accuracy improves with drawing quality. Clear, dimensioned PDF plans with structural annotations give the best results; Auto Quote can extract beam specifications, floor areas, and structural details to produce a precise, itemised quote. Photos of hand-drawn sketches or low-resolution scans work too, but with less precision. The Proper Tool's editable quote lets you check the scope, quantities and pricing before it goes to the client.",
  },
  {
    q: "What file types and plan formats work?",
    a: "The Proper Tool accepts PDF files (the most common format for architect's and engineer's drawings), as well as JPG, PNG, and WEBP images. Multi-page PDFs work; Auto Quote reads all pages. Structural drawing sets, floor plans, elevation drawings, and section views are all supported. For best results, use the original PDF rather than a scan of a printout.",
  },
  {
    q: "Does it produce a Bill of Quantities?",
    a: "The Proper Tool produces a fully itemised quote structured like a Bill of Quantities: every trade, every line item, with quantities and unit rates. It follows the NRM2 (New Rules of Measurement 2) conventions used across UK construction: works are ordered by trade and element, with labour, materials, and plant broken out per item. The output is not a formal NRM2 document, but it is structured and itemised in the same way, making it straightforward for quantity surveyors and contractors to review.",
  },
];

const RELATED_PAGES = [
  { href: "/ai-quoting-software/", label: "Smart Auto Quoting" },
  { href: "/quote-from-photo/", label: "Quote from Photo" },
  { href: "/ai-takeoff-software/", label: "Smart Takeoff Software" },
  { href: "/auto-quote/", label: "Auto Quote" },
  { href: "/quote-from-description/", label: "Quote from a Description" },
  { href: "/ai-construction-estimating-software/", label: "Construction Estimating" },
  { href: "/quoting-software-for-builders/", label: "Quoting Software for Builders" },
  { href: "/pricing/", label: "Pricing" },
  { href: "/free-trial/", label: "Free Trial" },
];

export default function QuoteFromDrawingsPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEOHead
        title="Quote from Drawings | Smart Takeoff Software for UK Builders | The Proper Tool"
        description="Upload your plans or PDF drawings and get a fully-priced construction quote for review. Smart takeoff and pricing built for UK builders."
        canonical="/quote-from-drawings"
        schema={[SOFTWARE_APP_SCHEMA, BREADCRUMB_SCHEMA, FAQ_SCHEMA]}
      />

      <SeoPageHeader />

      <main>
        {/* Hero */}
        <section className="py-16 sm:py-24 bg-gradient-to-b from-primary/5 to-background">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-6 justify-center flex-wrap">
              <Link href="/" className="hover:text-foreground transition-colors">Home</Link>
              <ChevronRight className="h-4 w-4" />
              <Link href="/ai-quoting-software" className="hover:text-foreground transition-colors">Smart Auto Quoting</Link>
              <ChevronRight className="h-4 w-4" />
              <span>Quote from Drawings</span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight tracking-tight mb-6">
              Smart takeoff and quoting from your drawings
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground leading-relaxed mb-8 max-w-3xl mx-auto">
              Upload your plans or PDF. The Proper Tool reads the drawings, extracts dimensions and structural details, and produces a fully-priced, professional quote. No manual measuring.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="lg" asChild>
                <Link href="/signup">
                  Start free, no card needed
                  <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <Link href="/pricing/">See pricing</Link>
              </Button>
            </div>
            <p className="text-sm text-muted-foreground mt-4">14-day free trial · 5 total quote creations across Auto, Template, and Manual quotes · No credit card required</p>
          </div>
        </section>

        {/* What is Smart takeoff */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">What is Smart takeoff and quoting from drawings?</h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-4">
              "Takeoff" is the process of extracting quantities from construction drawings: counting beams, measuring floor areas, reading spans, and listing every element of the build. Traditionally, a QS or estimator does this by hand with a ruler and spreadsheet. Smart takeoff replaces that manual process: Auto Quote reads the drawings and extracts the quantities automatically.
            </p>
            <p className="text-lg text-muted-foreground leading-relaxed mb-4">
              The Proper Tool goes one step further. It doesn't just extract quantities: it prices them. Upload a PDF of your structural drawings or architect's plans, and Auto Quote reads beam sizes, spans, floor areas, and structural details, then produces a fully-priced, itemised quote using UK construction rates.
            </p>
            <p className="text-lg text-muted-foreground leading-relaxed">
              The result: a professional quote your client expects, ready for you to review before sending.
            </p>
          </div>
        </section>

        {/* How it works */}
        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold mb-4">Upload plans or PDF → measured, priced output</h2>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Four steps from drawing set to client-ready quote.
              </p>
            </div>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { step: "1", title: "Upload your PDF or photos", desc: "Drop in your drawing set: structural engineer's drawings, architect's floor plans, or site photos. Multi-page PDFs supported." },
                { step: "2", title: "Auto Quote reads the drawings", desc: "Auto Quote extracts beam sizes, spans, floor areas, connection details, and project scope directly from the drawings." },
                { step: "3", title: "Itemised quote generated", desc: "Every trade, every line item, with quantities from the drawings and unit rates from the UK cost library, applied automatically." },
                { step: "4", title: "Review, edit, and send", desc: "Review every line, adjust any item, then export a branded PDF quote to send directly to your client." },
              ].map((item) => (
                <div key={item.step} className="bg-background rounded-xl border border-border p-6">
                  <div className="w-8 h-8 rounded-full bg-primary text-primary-foreground flex items-center justify-center font-bold text-sm mb-4">
                    {item.step}
                  </div>
                  <h3 className="font-semibold text-base mb-2">{item.title}</h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Old way vs AI way */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-8">The old way vs Auto Quote way</h2>
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="rounded-xl border border-border bg-muted/30 p-6">
                <h3 className="font-semibold text-base mb-4 text-muted-foreground">Manual takeoff</h3>
                <ul className="space-y-3">
                  {[
                    "Print drawings, get a ruler and scale",
                    "Measure spans and floor areas by hand",
                    "List every structural element manually",
                    "Look up rates in a spreadsheet or memory",
                    "Build the quote line by line",
                    "Format a PDF and send, hours later",
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <span className="mt-1 shrink-0 w-4 h-4 rounded-full bg-border flex items-center justify-center text-xs">×</span>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-xl border border-primary/30 bg-primary/5 p-6">
                <h3 className="font-semibold text-base mb-4 text-primary">Smart takeoff with The Proper Tool</h3>
                <ul className="space-y-3">
                  {[
                    "Upload the PDF, start with your drawing set",
                    "Auto Quote reads dimensions, beams, and areas",
                    "Every element extracted automatically",
                    "UK cost library applied instantly",
                    "Fully itemised quote generated",
                    "Review, edit, then send your quote",
                  ].map((item) => (
                    <li key={item} className="flex items-start gap-2 text-sm">
                      <CheckCircle2 className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* UK rates + BoQ */}
        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">UK rates + Bill of Quantities output</h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-4">
              Every quote The Proper Tool generates is priced from a UK construction cost library, built for residential construction, with Low, Typical, and High rate bands.
            </p>
            <p className="text-lg text-muted-foreground leading-relaxed mb-4">
              The output is structured like a Bill of Quantities (BoQ): every trade separated, every line item shown with quantity, unit, and rate. This follows NRM2 (New Rules of Measurement 2), the RICS standard for measuring and pricing building works used across UK construction. You don't need to know NRM2 to use The Proper Tool; Auto Quote handles the structure. But it means your output is immediately familiar to quantity surveyors, main contractors, and architects who review it.
            </p>
            <ul className="space-y-3 mb-8">
              {[
                "UK construction tasks across the supported trades",
                "Itemised by trade: groundworks through to decoration",
                "Quantities from drawings, rates from the UK cost library",
                "Labour, materials, and plant broken out per line item",
              ].map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <CheckCircle2 className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-muted-foreground">{item}</span>
                </li>
              ))}
            </ul>
            <Link href="/quoting-software-for-builders/" className="text-primary font-medium hover:underline flex items-center gap-1">
              <ChevronRight className="h-4 w-4" />
              More about quoting software for builders
            </Link>
          </div>
        </section>

        {/* Drawings or photos */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">Drawings or photos?</h2>
            <p className="text-lg text-muted-foreground leading-relaxed mb-6">
              Use whichever you have: both work, and they serve different situations.
            </p>
            <div className="grid sm:grid-cols-2 gap-6 mb-8">
              <div className="bg-background rounded-xl border border-border p-6">
                <FileText className="h-6 w-6 text-primary mb-3" />
                <h3 className="font-semibold text-base mb-2">PDF drawings give more accuracy</h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-3">
                  Structural drawings and architect's floor plans contain explicit dimensions, beam specifications, and structural annotations. Auto Quote extracts these precisely, giving you the most accurate takeoff. Use drawings when you have them, especially for structural work, extensions, and new builds.
                </p>
                <Link href="/ai-takeoff-software/" className="text-sm text-primary font-medium hover:underline flex items-center gap-1">
                  <ChevronRight className="h-3 w-3" />
                  Smart takeoff software
                </Link>
              </div>
              <div className="bg-background rounded-xl border border-border p-6">
                <Ruler className="h-6 w-6 text-primary mb-3" />
                <h3 className="font-semibold text-base mb-2">Photos work when drawings aren't available</h3>
                <p className="text-sm text-muted-foreground leading-relaxed mb-3">
                  On site without drawings? Photos of existing conditions, materials, and build complexity give Auto Quote enough context to generate a scope and price it. Less precise than drawings, but fast and practical for refurbs, fit-outs, and familiar job types.
                </p>
                <Link href="/quote-from-photo/" className="text-sm text-primary font-medium hover:underline flex items-center gap-1">
                  <ChevronRight className="h-3 w-3" />
                  Quote from photo
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* Feature highlights */}
        <section className="py-16 sm:py-20 bg-muted/30 border-t border-border/40">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-8">Built for structural work</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {[
                { icon: FileText, label: "Reads beam sizes, spans, and connection details from structural drawings" },
                { icon: ClipboardList, label: "Extracts floor areas, roof pitches, and dimensions from floor plans" },
                { icon: CheckCircle2, label: "Review scope, quantities and pricing before you send" },
                { icon: ChevronRight, label: "Fully editable: review every line item and adjust before export" },
              ].map(({ icon: Icon, label }) => (
                <div key={label} className="flex items-start gap-3 bg-background rounded-lg border border-border p-4">
                  <Icon className="h-5 w-5 text-primary flex-shrink-0 mt-0.5" />
                  <span className="text-sm text-muted-foreground">{label}</span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <h2 className="text-3xl sm:text-4xl font-bold mb-10 text-center">Frequently asked questions</h2>
            <div className="space-y-8">
              {FAQS.map((faq) => (
                <div key={faq.q} className="border-b border-border/60 pb-8 last:border-0 last:pb-0">
                  <h3 className="font-semibold text-lg mb-3">{faq.q}</h3>
                  <p className="text-muted-foreground leading-relaxed">{faq.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Internal links */}
        <section className="py-12 border-t border-border/40 bg-muted/20">
          <div className="max-w-4xl mx-auto px-4 sm:px-6">
            <h2 className="text-xl font-semibold mb-6">Related pages</h2>
            <div className="grid sm:grid-cols-3 gap-4">
              {RELATED_PAGES.map((link) => (
                <Link
                  key={link.href}
                  href={link.href}
                  className="flex items-start gap-2 text-sm text-primary hover:underline font-medium"
                >
                  <ChevronRight className="h-4 w-4 flex-shrink-0 mt-0.5" />
                  {link.label}
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="py-16 sm:py-20 border-t border-border/40">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">Upload your drawings. Get a priced quote.</h2>
            <p className="text-lg text-muted-foreground mb-8">
              Start your free 14-day trial. 5 total quote creations across Auto, Template, and Manual quotes included. No credit card required.
            </p>
            <Button size="lg" asChild>
              <Link href="/signup">
                Start your free trial
                <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
            <p className="text-sm text-muted-foreground mt-4">No credit card required · Cancel any time</p>
          </div>
        </section>
      </main>

      <footer className="border-t border-border/40 py-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-muted-foreground">
          <p>© {new Date().getFullYear()} The Proper Tool. Built for builders.</p>
          <div className="flex gap-6">
            <Link href="/privacy" className="hover:text-foreground transition-colors">Privacy</Link>
            <Link href="/terms" className="hover:text-foreground transition-colors">Terms</Link>
            <Link href="/blog" className="hover:text-foreground transition-colors">Blog &amp; Guides</Link>
            <Link href="/comparisons" className="hover:text-foreground transition-colors">Compare</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}
